import { getBranchData } from '@/lib/config/branches-config';
import { rewriteLegacyHtmlLinks } from '@/lib/seo/legacy-links';

/**
 * Справочник автозамен для плейсхолдеров в текстах
 */
export interface ReplacementMap {
  [key: string]: string;
}

interface CityForms {
  nominative: string;
  genitive: string;
  dative: string;
  accusative: string;
  instrumental: string;
  prepositional: string;
}

interface ItemInfoBlockLike {
  S_TITLE?: string | null;
  S_DESK?: string | null;
}

interface ProductDetailContentLike {
  descriptionHtml?: string | null;
  shortDescriptionHtml?: string | null;
}

interface CategoryPageInfoLike {
  description?: string | null;
  uf_top_desk?: string | null;
}

interface CategorySectionInfoLike {
  shortDescription?: string | null;
  faq?: FAQItem[] | null;
}

interface BlogAuthorLike {
  name?: string | null;
  role?: string | null;
  profileUrl?: string | null;
}

interface BlogSeoLike {
  TITLE?: string | null;
  DESCRIPTION?: string | null;
  KEYWORDS?: string | null;
  BROWSER_TITLE?: string | null;
}

interface BlogRelatedItemLike {
  title?: string | null;
  name?: string | null;
  NAME?: string | null;
  description?: string | null;
  previewText?: string | null;
  PREVIEW_TEXT?: string | null;
}

interface BlogDetailLike {
  title?: string | null;
  name?: string | null;
  NAME?: string | null;
  description?: string | null;
  previewText?: string | null;
  PREVIEW_TEXT?: string | null;
  contentHtml?: string | null;
  content?: string | null;
  DETAIL_TEXT?: string | null;
  author?: BlogAuthorLike | null;
  SEO?: BlogSeoLike | null;
  blocks?: ItemInfoBlockLike[] | null;
  relatedBlogs?: BlogRelatedItemLike[] | null;
  relatedCatalogItems?: BlogRelatedItemLike[] | null;
  relatedCatalogSections?: BlogRelatedItemLike[] | null;
}

const BRAND_FORMS = {
  nominative: 'ЗМК Континенталь',
  genitive: 'ЗМК Континенталь',
  dative: 'ЗМК Континенталь',
  accusative: 'ЗМК Континенталь',
  instrumental: 'ЗМК Континенталь',
  prepositional: 'о ЗМК Континенталь',
};

function getDefaultCityForms(cityName: string): CityForms {
  if (cityName.endsWith('а')) {
    const stem = cityName.slice(0, -1);

    return {
      nominative: cityName,
      genitive: `${stem}ы`,
      dative: `${stem}е`,
      accusative: `${stem}у`,
      instrumental: `${stem}ой`,
      prepositional: `в ${stem}е`,
    };
  }

  if (cityName.endsWith('бург')) {
    return {
      nominative: cityName,
      genitive: `${cityName}а`,
      dative: `${cityName}у`,
      accusative: cityName,
      instrumental: `${cityName}ом`,
      prepositional: `в ${cityName}е`,
    };
  }

  return {
    nominative: cityName,
    genitive: cityName,
    dative: cityName,
    accusative: cityName,
    instrumental: cityName,
    prepositional: `в ${cityName}`,
  };
}

/**
 * Часть SEO-шаблонов в Битриксе написана как «в #city#» / «в #region#», где
 * #city# и #region# — именительный падеж. После подстановки получалось
 * «в Москва» — в том числе в <title> московской главной. Правильные формы
 * (#citypred# уже с предлогом, #region_dat# совпадает с предложным) подставляем
 * на уровне шаблона, до основной замены: править сотни записей в CMS дороже,
 * а ошибка видна прямо в выдаче.
 */
function normalizeCasesAfterPrepositions(text: string, replacements: ReplacementMap): string {
  let result = text;

  const cityPrepositional = replacements['#citypred#'];
  if (cityPrepositional) {
    const capitalized = cityPrepositional.charAt(0).toUpperCase() + cityPrepositional.slice(1);
    result = result.replace(
      /(^|[^А-Яа-яЁё#])([Вв]о?)\s+#city#/gu,
      (_match, before: string, preposition: string) =>
        `${before}${preposition[0] === 'В' ? capitalized : cityPrepositional}`
    );
  }

  const regionDative = replacements['#region_dat#'];
  if (regionDative) {
    result = result.replace(
      /(^|[^А-Яа-яЁё#])([Вв]о?)\s+#region#/gu,
      (_match, before: string, preposition: string) => `${before}${preposition} ${regionDative}`
    );
  }

  return result;
}

function applyReplacements(text: string, replacements: ReplacementMap): string {
  let result = normalizeCasesAfterPrepositions(text, replacements);

  for (const [placeholder, value] of Object.entries(replacements)) {
    result = result.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
  }

  return result.replace(
    /(^|\s)(в|во|по|на|к|ко|с|со|из|от|до|под|над|об|обо|о)\s+\2(?=\s|$)/giu,
    '$1$2'
  );
}

/**
 * Получает справочник автозамен на основе текущего региона
 * @param branchCode - код региона (например, 'msk', 'spb')
 * @returns объект с плейсхолдерами и их значениями
 */
export function getReplacementMap(branchCode: string): ReplacementMap {
  const branch = getBranchData(branchCode) || getBranchData('spb');
  const cityName = branch?.name || 'Санкт-Петербург';
  const cityForms = branch?.placeholders?.city || getDefaultCityForms(cityName);
  const regionForms = branch?.placeholders?.region || {
    nominative: '',
    genitive: '',
    dative: '',
  };

  const replacements: ReplacementMap = {
    '#city#': cityForms.nominative,
    '#citygen#': cityForms.genitive,
    '#citydat#': cityForms.dative,
    '#cityacc#': cityForms.accusative,
    '#cityins#': cityForms.instrumental,
    '#citypred#': cityForms.prepositional,
    '#corebrend#': BRAND_FORMS.nominative,
    '#corebrend_gen#': BRAND_FORMS.genitive,
    '#corebrend_dat#': BRAND_FORMS.dative,
    '#corebrend_acc#': BRAND_FORMS.accusative,
    '#corebrend_ins#': BRAND_FORMS.instrumental,
    '#corebrend_pred#': BRAND_FORMS.prepositional,
    '#region#': regionForms.nominative,
    '#region_gen#': regionForms.genitive,
    '#region_dat#': regionForms.dative || regionForms.genitive,
    '#country#': branch?.placeholders?.country || 'Россия',
    '#direction#': branch?.placeholders?.direction || 'по всей России',
  };

  return replacements;
}

/**
 * Заменяет плейсхолдеры в тексте на значения из справочника
 * @param text - текст для обработки
 * @param branchCode - код региона
 * @returns текст с замененными плейсхолдерами
 */
export function replacePlaceholders(text: string | undefined | null, branchCode: string): string {
  if (!text || typeof text !== 'string') {
    return text || '';
  }

  return applyReplacements(text, getReplacementMap(branchCode));
}

export function replacePlaceholdersInItemInfoBlocks<T extends ItemInfoBlockLike>(
  blocks: T[] | undefined | null,
  branchCode: string
): T[] {
  if (!blocks || !Array.isArray(blocks) || blocks.length === 0) {
    return [];
  }

  const replacements = getReplacementMap(branchCode);

  return blocks.map((block) => ({
    ...block,
    S_TITLE: typeof block.S_TITLE === 'string' ? applyReplacements(block.S_TITLE, replacements) : block.S_TITLE,
    S_DESK: typeof block.S_DESK === 'string' ? rewriteLegacyHtmlLinks(applyReplacements(block.S_DESK, replacements)) : block.S_DESK,
  }));
}

export function replacePlaceholdersInProductDetailData<T extends ProductDetailContentLike>(
  detailData: T | undefined | null,
  branchCode: string
): T | undefined | null {
  if (!detailData || typeof detailData !== 'object') {
    return detailData;
  }

  const replacements = getReplacementMap(branchCode);

  return {
    ...detailData,
    descriptionHtml:
      typeof detailData.descriptionHtml === 'string'
        ? rewriteLegacyHtmlLinks(applyReplacements(detailData.descriptionHtml, replacements))
        : detailData.descriptionHtml,
    shortDescriptionHtml:
      typeof detailData.shortDescriptionHtml === 'string'
        ? rewriteLegacyHtmlLinks(applyReplacements(detailData.shortDescriptionHtml, replacements))
        : detailData.shortDescriptionHtml,
  };
}

export function replacePlaceholdersInCategoryPageInfo<T extends CategoryPageInfoLike>(
  categoryInfo: T | undefined | null,
  branchCode: string
): T | undefined | null {
  if (!categoryInfo || typeof categoryInfo !== 'object') {
    return categoryInfo;
  }

  const replacements = getReplacementMap(branchCode);

  return {
    ...categoryInfo,
    description:
      typeof categoryInfo.description === 'string'
        ? rewriteLegacyHtmlLinks(applyReplacements(categoryInfo.description, replacements))
        : categoryInfo.description,
    uf_top_desk:
      typeof categoryInfo.uf_top_desk === 'string'
        ? rewriteLegacyHtmlLinks(applyReplacements(categoryInfo.uf_top_desk, replacements))
        : categoryInfo.uf_top_desk,
  };
}

export function replacePlaceholdersInCategorySectionInfo<T extends CategorySectionInfoLike>(
  sectionInfo: T | undefined | null,
  branchCode: string
): T | undefined | null {
  if (!sectionInfo || typeof sectionInfo !== 'object') {
    return sectionInfo;
  }

  const replacements = getReplacementMap(branchCode);

  return {
    ...sectionInfo,
    shortDescription:
      typeof sectionInfo.shortDescription === 'string'
        ? rewriteLegacyHtmlLinks(applyReplacements(sectionInfo.shortDescription, replacements))
        : sectionInfo.shortDescription,
    faq: Array.isArray(sectionInfo.faq)
      ? sectionInfo.faq.map((item) => ({
          ...item,
          title: typeof item?.title === 'string' ? applyReplacements(item.title, replacements) : item?.title,
          description:
            typeof item?.description === 'string'
              ? applyReplacements(item.description, replacements)
              : item?.description,
        }))
      : sectionInfo.faq,
  };
}

function replacePlaceholdersInBlogRelatedCollection<T extends BlogRelatedItemLike>(
  items: T[] | undefined | null,
  replacements: ReplacementMap
): T[] {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return [];
  }

  return items.map((item) => ({
    ...item,
    title: typeof item.title === 'string' ? applyReplacements(item.title, replacements) : item.title,
    name: typeof item.name === 'string' ? applyReplacements(item.name, replacements) : item.name,
    NAME: typeof item.NAME === 'string' ? applyReplacements(item.NAME, replacements) : item.NAME,
    description:
      typeof item.description === 'string' ? applyReplacements(item.description, replacements) : item.description,
    previewText:
      typeof item.previewText === 'string' ? applyReplacements(item.previewText, replacements) : item.previewText,
    PREVIEW_TEXT:
      typeof item.PREVIEW_TEXT === 'string' ? applyReplacements(item.PREVIEW_TEXT, replacements) : item.PREVIEW_TEXT,
  }));
}

export function replacePlaceholdersInBlogDetailData<T extends BlogDetailLike>(
  detailData: T | undefined | null,
  branchCode: string
): T | undefined | null {
  if (!detailData || typeof detailData !== 'object') {
    return detailData;
  }

  const replacements = getReplacementMap(branchCode);

  return {
    ...detailData,
    title: typeof detailData.title === 'string' ? applyReplacements(detailData.title, replacements) : detailData.title,
    name: typeof detailData.name === 'string' ? applyReplacements(detailData.name, replacements) : detailData.name,
    NAME: typeof detailData.NAME === 'string' ? applyReplacements(detailData.NAME, replacements) : detailData.NAME,
    description:
      typeof detailData.description === 'string'
        ? applyReplacements(detailData.description, replacements)
        : detailData.description,
    previewText:
      typeof detailData.previewText === 'string'
        ? applyReplacements(detailData.previewText, replacements)
        : detailData.previewText,
    PREVIEW_TEXT:
      typeof detailData.PREVIEW_TEXT === 'string'
        ? applyReplacements(detailData.PREVIEW_TEXT, replacements)
        : detailData.PREVIEW_TEXT,
    contentHtml:
      typeof detailData.contentHtml === 'string'
        ? applyReplacements(detailData.contentHtml, replacements)
        : detailData.contentHtml,
    content:
      typeof detailData.content === 'string' ? applyReplacements(detailData.content, replacements) : detailData.content,
    DETAIL_TEXT:
      typeof detailData.DETAIL_TEXT === 'string'
        ? applyReplacements(detailData.DETAIL_TEXT, replacements)
        : detailData.DETAIL_TEXT,
    author: detailData.author
      ? {
          ...detailData.author,
          name:
            typeof detailData.author.name === 'string'
              ? applyReplacements(detailData.author.name, replacements)
              : detailData.author.name,
          role:
            typeof detailData.author.role === 'string'
              ? applyReplacements(detailData.author.role, replacements)
              : detailData.author.role,
          profileUrl:
            typeof detailData.author.profileUrl === 'string'
              ? applyReplacements(detailData.author.profileUrl, replacements)
              : detailData.author.profileUrl,
        }
      : detailData.author,
    SEO: detailData.SEO
      ? {
          ...detailData.SEO,
          TITLE:
            typeof detailData.SEO.TITLE === 'string'
              ? applyReplacements(detailData.SEO.TITLE, replacements)
              : detailData.SEO.TITLE,
          DESCRIPTION:
            typeof detailData.SEO.DESCRIPTION === 'string'
              ? applyReplacements(detailData.SEO.DESCRIPTION, replacements)
              : detailData.SEO.DESCRIPTION,
          KEYWORDS:
            typeof detailData.SEO.KEYWORDS === 'string'
              ? applyReplacements(detailData.SEO.KEYWORDS, replacements)
              : detailData.SEO.KEYWORDS,
          BROWSER_TITLE:
            typeof detailData.SEO.BROWSER_TITLE === 'string'
              ? applyReplacements(detailData.SEO.BROWSER_TITLE, replacements)
              : detailData.SEO.BROWSER_TITLE,
        }
      : detailData.SEO,
    blocks: detailData.blocks ? replacePlaceholdersInItemInfoBlocks(detailData.blocks, branchCode) : detailData.blocks,
    relatedBlogs: detailData.relatedBlogs
      ? replacePlaceholdersInBlogRelatedCollection(detailData.relatedBlogs, replacements)
      : detailData.relatedBlogs,
    relatedCatalogItems: detailData.relatedCatalogItems
      ? replacePlaceholdersInBlogRelatedCollection(detailData.relatedCatalogItems, replacements)
      : detailData.relatedCatalogItems,
    relatedCatalogSections: detailData.relatedCatalogSections
      ? replacePlaceholdersInBlogRelatedCollection(detailData.relatedCatalogSections, replacements)
      : detailData.relatedCatalogSections,
  };
}

/**
 * Интерфейс для элемента FAQ
 */
export interface FAQItem {
  title: string;
  description: string;
}

/**
 * Заменяет плейсхолдер #name_item# на название товара в FAQ элементах
 * и подставляет городские/брендовые плейсхолдеры (#city#, #citypred#, и т.д.)
 * @param faq - массив FAQ элементов
 * @param productName - название товара
 * @param branchCode - код региона для подстановки городских плейсхолдеров
 * @returns обработанный массив FAQ с замененными плейсхолдерами
 */
export function replaceItemNameInFAQ(
  faq: FAQItem[] | undefined | null,
  productName: string,
  branchCode?: string
): FAQItem[] {
  if (!faq || !Array.isArray(faq) || faq.length === 0) {
    return [];
  }

  const branchReplacements = branchCode ? getReplacementMap(branchCode) : null;
  const hasProductName = productName && typeof productName === 'string';
  const placeholderRegex = hasProductName
    ? new RegExp('#name_item#'.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g')
    : null;

  const transform = (value: string): string => {
    let result = value;
    if (placeholderRegex) {
      result = result.replace(placeholderRegex, productName);
    }
    if (branchReplacements) {
      result = applyReplacements(result, branchReplacements);
    }
    return result;
  };

  return faq.map((item) => ({
    title: typeof item.title === 'string' ? transform(item.title) : item.title,
    description: typeof item.description === 'string' ? transform(item.description) : item.description,
  }));
}
